import {
  createEventCallbackRedirect,
  EVENT_TIMEOUT_IN_MS,
  sendGAEvent,
} from '@noths/polaris-client-google-analytics';
import {
  CarouselProductClickPayload,
  CarouselProductVisiblePayload,
} from '@noths/polaris-client-ribbons-design-system';
import { getProductIdFromProductCode } from '@noths/polaris-client-utils';
import { BaseFieldObject, Price } from '@noths/polaris-dev-ts-types';

import { Product } from '../../../types/Product';

interface GenerateProductsEventParams {
  products: Product[];
  placement?: string;
  placementStrategy?: string;
  positionInList?: number;
  personalisationsTotalAmount?: number;
}

const getPriceInGBP = (prices: Price[]) => {
  const priceInGBP = prices.find(({ currency }) => currency === 'GBP');

  return priceInGBP?.amount
    ? new Intl.NumberFormat('en-GB', {
        style: 'decimal',
        minimumFractionDigits: 2,
      }).format(priceInGBP.amount / 100)
    : '';
};

const generateProductsForEvent = ({
  products,
  placement,
  placementStrategy,
  positionInList,
}: GenerateProductsEventParams): BaseFieldObject[] =>
  products.map(
    (
      {
        free_domestic_delivery,
        new: isNew,
        on_sale,
        purchasable,
        sale_percentage,
        prices,
        title,
        code,
        partner,
      },
      index,
    ) => {
      const dimension22List = [
        free_domestic_delivery && 'free uk delivery',
        isNew && 'new',
        on_sale && !!sale_percentage && `-${sale_percentage}% off`,
        !purchasable && 'out of stock',
      ]
        .filter(Boolean)
        .join(', ');

      const productPrice = getPriceInGBP(prices);
      const position = positionInList || index + 1;
      const list = placementStrategy
        ? `product - rich relevance - ${placement} - ${placementStrategy}`
        : `product - rich relevance - ${placement}`;

      return {
        name: title,
        id: String(getProductIdFromProductCode(code)),
        dimension22: dimension22List,
        dimension25: position,
        dimension30: String(code),
        dimension31: productPrice,
        price: productPrice,
        dimension32: productPrice,
        dimension33: on_sale ? `${sale_percentage}.0%` : 'none',
        brand: partner.name,
        dimension34: partner.shortcode,
        position: position,
        list,
        dimension16: 'not set',
      };
    },
  );

export const sendProductClickEvent = (
  payload: CarouselProductClickPayload,
  products: Product[],
  placementStrategy: string,
) => {
  const { productCode, placement, productUrl } = payload;

  if (products) {
    const productIndex = products?.findIndex(({ code }) => productCode === code);
    const positionInList = productIndex + 1;
    const product = products[productIndex];
    const eventLabel = `${positionInList} : ${placementStrategy || ''} : ${placement}`;

    sendGAEvent({
      event: 'product_clicks',
      event_category: 'Rich Relevance',
      event_action: 'Product Click',
      event_label: eventLabel,
      eventCallback: createEventCallbackRedirect(productUrl),
      eventTimeout: EVENT_TIMEOUT_IN_MS,
      algonomy_tracking_URL: product.linkURL,
      ecommerce: {
        click: {
          actionField: {
            list: `product - rich relevance - ${placement} - ${placementStrategy}`,
          },
          products: generateProductsForEvent({
            products: [product!],
            placement,
            placementStrategy,
            positionInList,
          }),
        },
      },
    });
  }
};

export const sendProductShelfImpressionEvent = (
  payload: CarouselProductVisiblePayload,
  products: Product[],
  placementStrategy: string,
) => {
  const { placement } = payload;

  if (products) {
    const eventLabel = `${placementStrategy || ''} : ${placement}`;
    sendGAEvent({
      event: 'product_impressions',
      event_category: 'Rich Relevance',
      event_action: 'Product Impressions',
      event_label: eventLabel,
      ecommerce: {
        impressions: generateProductsForEvent({
          products: products,
          placement,
          placementStrategy,
        }),
      },
    });
  }
};
